import { useEffect, useMemo, useState } from 'react';
import { Dialog, InputBase } from '@mui/material';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isMac } from '../../../lib/platform';
import { latinize } from '../../../lib/utils';
import LinkWrapper from '../../LinkWrapper';
import { flattenMenusForSearch } from './flattenMenusForSearch';
import { menuLabel } from './menuLabel';
import {
    SIDEBAR_SECTION_LABELS,
    SIDEBAR_SECTION_ORDER,
} from './sidebarSections';
import { hrefFor, useMenuNavigation } from './useMenuNavigation';
import type { Menu } from './types';

interface SidebarSearchProps {
    menus: Menu[];
}

const normalize = (value: string): string => latinize(value).toLowerCase();

/**
 * Buscador de menús del sidebar: botón con atajo ⌘K / Ctrl+K y diálogo con la
 * lista de menús aplanada y agrupada por sección, igual que en
 * sporttia-competitions.
 */
export default function SidebarSearch({
    menus,
}: SidebarSearchProps): React.ReactElement {
    const { t } = useTranslation();
    const { go } = useMenuNavigation();
    const [open, setOpen] = useState<boolean>(false);
    const [query, setQuery] = useState<string>('');

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                setOpen((previous) => !previous);
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, []);

    const entries = useMemo(() => flattenMenusForSearch(menus), [menus]);

    const groups = useMemo(() => {
        const needle = normalize(query.trim());

        const matching = needle
            ? entries.filter((entry) =>
                  normalize(
                      `${entry.parentCaption ? `${menuLabel(t, entry.parentCaption)} ` : ''}${menuLabel(t, entry.caption)}`,
                  ).includes(needle),
              )
            : entries;

        return SIDEBAR_SECTION_ORDER.map((section) => ({
            section,
            items: matching.filter((entry) => entry.section === section),
        })).filter((group) => group.items.length > 0);
    }, [entries, query, t]);

    const close = (): void => {
        setOpen(false);
        setQuery('');
    };

    return (
        <>
            <button
                type="button"
                id="tour-sidebar-search"
                onClick={() => setOpen(true)}
                aria-label={t('sidebar.search')}
                className="border-sidebar-border bg-sidebar-accent/30 text-sidebar-foreground/70 hover:bg-sidebar-accent/50 flex w-full cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors"
            >
                <Search className="h-4 w-4" />
                <span className="flex-1 text-left">{t('sidebar.search')}</span>
                <kbd className="border-sidebar-border bg-sidebar text-sidebar-foreground/60 pointer-events-none inline-flex h-5 items-center gap-0.5 rounded border px-1.5 text-xs font-medium">
                    {isMac() ? '⌘K' : 'Ctrl K'}
                </kbd>
            </button>

            <Dialog
                open={open}
                onClose={close}
                fullWidth
                maxWidth="sm"
                aria-label={t('sidebar.search')}
            >
                <div className="border-border flex items-center gap-2 border-b px-4 py-3">
                    <Search className="h-4 w-4 opacity-60" />
                    <InputBase
                        autoFocus
                        fullWidth
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t('sidebar.searchPlaceholder')}
                        inputProps={{ 'aria-label': t('sidebar.search') }}
                    />
                </div>

                <div className="max-h-96 overflow-y-auto p-2">
                    {groups.length === 0 && (
                        <p className="text-muted-foreground px-2 py-6 text-center text-sm">
                            {t('sidebar.searchEmpty')}
                        </p>
                    )}

                    {groups.map(({ section, items }) => (
                        <div key={section} className="mb-2">
                            <p className="text-muted-foreground px-2 py-1 text-xs font-medium tracking-wider uppercase">
                                {t(SIDEBAR_SECTION_LABELS[section])}
                            </p>
                            <ul className="flex flex-col">
                                {items.map((entry) => (
                                    <li
                                        key={`${section}-${entry.path ?? entry.url}-${entry.caption}`}
                                    >
                                        <LinkWrapper
                                            href={hrefFor(entry)}
                                            callback={() => {
                                                close();
                                                go(entry);
                                            }}
                                            className="hover:bg-accent flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors"
                                        >
                                            {entry.parentCaption && (
                                                <span className="text-muted-foreground">
                                                    {menuLabel(
                                                        t,
                                                        entry.parentCaption,
                                                    )}
                                                    {' › '}
                                                </span>
                                            )}
                                            <span>
                                                {menuLabel(t, entry.caption)}
                                            </span>
                                        </LinkWrapper>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </Dialog>
        </>
    );
}
